import type { Gender, ProfileField, ProfileFields, ProfilePatch, ProfileRequiredField, Role } from '../types'

export const ROLE_LABELS: Record<Role, string> = {
	super_admin: 'Super Admin',
	owner: 'Owner',
	manager: 'Manager',
	shift_lead: 'Shift Lead',
	staff: 'Staff',
}

export const GENDER_LABELS: Record<Gender, string> = {
	female: 'Female',
	male: 'Male',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function initialsOf(name: string): string {
	const words = name.trim().split(/\s+/).filter(Boolean)
	if (words.length === 0) return '?'
	const first = words[0].charAt(0)
	const last = words.length > 1 ? words[words.length - 1].charAt(0) : words[0].charAt(1)
	return `${first}${last}`.toUpperCase()
}

export function formatBirthday(monthDay: string): string {
	const [month, day] = monthDay.split('-').map(Number)
	return `${day} ${MONTHS[month - 1] ?? ''}`
}

export function formatPhone(e164: string): string {
	return e164.startsWith('+84') ? `0${e164.slice(3)}` : e164
}

export type FormField = ProfileField | 'name'

export type ProfileDraft = Record<FormField, string>

export const COMPLETION_FIELDS: readonly ProfileRequiredField[] = [
	'name',
	'phone',
	'legal_name',
	'gender',
	'date_of_birth',
	'address',
	'id_number',
	'id_issued_on',
	'id_issued_by',
	'emergency_name',
	'emergency_relation',
	'emergency_phone',
]

export const MEMBER_PROFILE_FIELDS: readonly FormField[] = [...COMPLETION_FIELDS, 'start_date']

export const FIELD_LABELS: Record<FormField, string> = {
	name: 'Display name',
	phone: 'Phone',
	legal_name: 'Full legal name',
	gender: 'Gender',
	date_of_birth: 'Date of birth',
	address: 'Address',
	id_number: 'ID number',
	id_issued_on: 'ID issue date',
	id_issued_by: 'ID issued by',
	emergency_name: 'Emergency contact',
	emergency_relation: 'Emergency relationship',
	emergency_phone: 'Emergency phone',
	start_date: 'First working day',
}

export function needsDisplayName(missing: readonly ProfileRequiredField[] | undefined): boolean {
	return missing?.includes('name') ?? false
}

export const MANAGER_ONLY_HINT = 'Locked once entered. Ask a manager to change it.'

export function toDraft(profile: ProfileFields, name: string): ProfileDraft {
	const entries = Object.entries(profile).map(([field, value]) => [
		field,
		field.endsWith('phone') && value ? formatPhone(value) : (value ?? ''),
	])
	return { ...(Object.fromEntries(entries) as Record<ProfileField, string>), name }
}

export function changedFields(initial: ProfileDraft, draft: ProfileDraft, editable: readonly FormField[]): ProfilePatch {
	const changed = editable.filter((field) => draft[field].trim() !== initial[field].trim())
	return Object.fromEntries(changed.map((field) => [field, draft[field].trim() === '' ? null : draft[field].trim()])) as ProfilePatch
}

export function emptyRequiredFields(draft: ProfileDraft, required: readonly ProfileRequiredField[]): ProfileRequiredField[] {
	return required.filter((field) => draft[field].trim() === '')
}
